import * as Updates from 'expo-updates';
import * as storage from '../../utils/storage';
import Loading from '../Loading/Loading';
import NeedUpdate from '../../app/(public)/need-update';
import { AppState, SafeAreaView, View } from 'react-native';
import { ConfirmDialog } from '../Modal/ConfirmDialog';
import { GET_LATEST_COMPATIBLE_VERSION, GET_MAINTENANCE } from '../../state/queries/Root';
import { Maintenance } from './Maintenance';
import { PublicPagesHeader } from '../Header/PublicPages';
import { captureException } from '../../utils/sentry/sentry';
import { compare } from 'compare-versions';
import { getChannel } from '../../config/config';
import { isAndroid, isIos, isWeb } from '../../config/designSystem/Layout';
import { observer } from 'mobx-react';
import { showMessage } from 'react-native-flash-message';
import   { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useStores } from '../../hooks/useStores';

type MaintenanceFlags = {
  maintenanceIos?: boolean | null;
  maintenanceAndroid?: boolean | null;
  maintenanceWeb?: boolean | null;
  maintenanceIosBelowVersion?: string | null;
  maintenanceAndroidBelowVersion?: string | null;
};

// A mobile build is in maintenance only when its platform switch is on AND its version is below
// the threshold next to it, so a newer build in store review keeps working. No threshold, or a
// version that cannot be compared, means no maintenance. Web has no version.
const isVersionBelow = (version: string | null, threshold?: string | null): boolean => {
  if (!version || !threshold?.trim()) {
    return false;
  }
  try {
    return compare(version, threshold.trim(), '<');
  } catch (error) {
    return false;
  }
};

const isUnderMaintenance = (flags: MaintenanceFlags, version: string | null): boolean => !!(
  (isIos && flags.maintenanceIos && isVersionBelow(version, flags.maintenanceIosBelowVersion))
  || (isAndroid && flags.maintenanceAndroid && isVersionBelow(version, flags.maintenanceAndroidBelowVersion))
  || (isWeb && flags.maintenanceWeb)
);

export const UpdateApp = observer(({children}) => {
  const rootStore = useStores();
  const labels = rootStore?.language.translate.MOBILE?.ROOT?.NEW_VERSION_AVAILABLE;

  const [downloadInProgress, setDownloadInProgress] = useState<boolean>(false);
  const [updateAvailable, setUpdateAvailable] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [latestCompatibleVersion, setLatestCompatibleVersion] = useState<string>('');


  const runTimeVersion : string | null = Updates.runtimeVersion;

  const {data: settingsVariables} = useQuery(GET_LATEST_COMPATIBLE_VERSION, {
    fetchPolicy:'cache-first',
  });

  useEffect(()=> {
    if(settingsVariables) {
      setLatestCompatibleVersion(settingsVariables.settingsVariables.items[0].latestCompatibleVersion);
    }
  },[settingsVariables]);

  // Maintenance switches and version thresholds (admin > General settings). Always read from the
  // network so turning one on or off reaches users without a restart: again each time the app
  // comes back to the foreground, and every minute while the maintenance screen is up so it
  // lifts by itself. A failed query (offline, API without the fields) never blocks the app.
  // The last answer is kept in state, not read from `data`: ApolloApp builds a new client on
  // each of its renders, which empties `data` until the new client has refetched.
  const [maintenanceFlags, setMaintenanceFlags] = useState<MaintenanceFlags | null>(null);
  const {data: maintenanceData, refetch: refetchMaintenance} = useQuery(GET_MAINTENANCE, {
    fetchPolicy: 'network-only',
    pollInterval: maintenanceFlags && isUnderMaintenance(maintenanceFlags, runTimeVersion) ? 60 * 1000 : 0,
  });

  useEffect(() => {
    const flags = maintenanceData?.settingsVariables?.items?.[0];
    if (flags) {
      setMaintenanceFlags(flags);
    }
  }, [maintenanceData]);

  const underMaintenance = !!maintenanceFlags && isUnderMaintenance(maintenanceFlags, runTimeVersion);

  const retryMaintenance = () => {
    refetchMaintenance().catch(() => {});
  };

  useEffect(() => {
    const subscription = AppState.addEventListener('change', state => {
      if (state === 'active') {
        refetchMaintenance().catch(() => {});
      }
    });
    return () => subscription.remove();
  }, [refetchMaintenance]);


  // Rendered in place of the app rather than navigated to: RedirectApp sends a signed-in
  // user away from every onlyPublicKeys route (need-update included) back to the dashboard,
  // so a navigation-based gate never held for logged-in users. Replacing children leaves
  // nothing to navigate away to.
  const mustUpdate = !isWeb && !updateAvailable && !!runTimeVersion && !!latestCompatibleVersion
    && !compare(latestCompatibleVersion, runTimeVersion, '<=');

  useEffect(() => {
    if (updateAvailable) {
      setShowAlert(true);
    } else {
      setShowAlert(false);
    }
  }, [updateAvailable]);

  useEffect(()=>{
    if (!isWeb) {
      checkUpdate();
    }
  }, []);

  const downloadNewVersion = async () => {
    setDownloadInProgress(true);
    try {
      await Updates.fetchUpdateAsync();
      await Updates.reloadAsync();
    } catch (e) {
      captureException(e);
    } finally {
      setDownloadInProgress(false);
      showMessage({
        message: labels?.FLASH_CONGRATS ? labels.FLASH_CONGRATS : 'Congrats, you are on the latest version',
        type: 'success',
        style: { backgroundColor: 'white' },
        duration: 5000,
      });
    }
  };

  const checkForUpdate = async () => {
    try {
      const update = await Updates.checkForUpdateAsync();
      setUpdateAvailable(update?.isAvailable);
    } catch(error) {
      console.log(error);
      setUpdateAvailable(false);
    } finally {
      storage.saveString('timeUpdateAsked', String(new Date().getTime()));
    }
  };

  const checkUpdate = async() => {
    const channel = getChannel();
    if (!__DEV__ && channel !== 'dev' && channel !== 'devfordevice') {
      const timeUpdateAskedString = await storage.loadString('timeUpdateAsked');
      if (timeUpdateAskedString) {
        const timeUpdateAsked = Number(timeUpdateAskedString);
        const minutesDifference = Math.abs(new Date().getTime() - timeUpdateAsked) / 60000;
        if (minutesDifference > 60 * 24) {
          checkForUpdate();
        }
      } else {
        checkForUpdate();
      }
    }
  };


  if (downloadInProgress) {
    return (
      <Loading
        withLabel={true}
        label={
          labels?.DOWNLOADING
            ? labels?.DOWNLOADING
            : 'Downloading new version...'
        }
      />
    );
  }



  if (underMaintenance) {
    return <Maintenance onRetry={retryMaintenance} />;
  }

  if (mustUpdate) {
    // same header as the (public) stack gives the need-update route, minus the support icon
    return (
      <View style={{ flex: 1, backgroundColor: 'white' }}>
        <SafeAreaView style={{ paddingHorizontal: 40, backgroundColor: 'white' }}>
          <PublicPagesHeader onPress={() => {}} withContactToSupport={false} />
        </SafeAreaView>
        <NeedUpdate />
      </View>
    );
  }

  return(<>
    <ConfirmDialog
      visible={showAlert}
      title={labels?.TITLE}
      message={labels?.MAIN}
      cancelText={labels?.FIRST_ANSWER}
      confirmText={labels?.SECOND_ANSWER}
      onCancel={() => {
        setShowAlert(false);
      }}
      onConfirm={async () => {
        downloadNewVersion();
        setShowAlert(false);
      }}
    />
    {children}
  </>);

});
